import { z } from 'zod';
// QA rubric criteria and sentiment labels: what the QA evaluator scores every call and chat against. Both apply only
// to the agents they're assigned to, and only while active.
const agentsSchema = z
    .union([z.literal('all'), z.array(z.string())])
    .describe('"all" for every current agent, agent ids from list_agents, or [] for none');
export const SENTIMENT_COLOR_NAMES = ['green', 'yellow', 'red', 'blue', 'orange', 'gray'];
const rubricViewSchema = z.object({
    criterionId: z.string(),
    name: z.string(),
    description: z.string().optional(),
    prompt: z.string().optional(),
    passedLabel: z.string(),
    failedLabel: z.string(),
    active: z.boolean(),
    agentIds: z.array(z.string())
});
const sentimentLabelViewSchema = z.object({
    labelId: z.string(),
    label: z.string(),
    description: z.string().optional(),
    color: z.string(),
    active: z.boolean(),
    agentIds: z.array(z.string())
});
export const listQaRubricsToolSchema = {
    name: 'list_qa_rubrics',
    description: "List the organization's QA rubric criteria: each one's evaluation prompt, pass and fail labels, whether it's active, and the agents it scores.",
    inputSchema: z.object({}),
    outputSchema: z.object({ criteria: z.array(rubricViewSchema) })
};
export const createQaRubricToolSchema = {
    name: 'create_qa_rubric',
    description: 'Create a QA rubric criterion, evaluated as pass or fail on every call and chat of the agents it is assigned to once it is active.',
    inputSchema: z.object({
        name: z.string().min(1).describe('Short and unique, such as "Confirms the policy number"'),
        prompt: z
            .string()
            .min(1)
            .describe('What the evaluator checks: the behavior, and what counts as a pass and as a fail'),
        description: z
            .string()
            .optional()
            .describe('For people reading the results; the evaluator never sees it'),
        agents: agentsSchema,
        passedLabel: z.string().max(50).optional().describe('Defaults to "Pass"'),
        failedLabel: z.string().max(50).optional().describe('Defaults to "Fail"'),
        active: z.boolean().optional().describe('Defaults to true')
    }),
    outputSchema: z.object({ criterionId: z.string(), name: z.string() })
};
export const updateQaRubricToolSchema = {
    name: 'update_qa_rubric',
    description: "Change a QA rubric criterion. Leave out what shouldn't change.",
    inputSchema: createQaRubricToolSchema.inputSchema.partial().extend({ criterionId: z.string() }),
    outputSchema: z.object({ criterionId: z.string() })
};
export const archiveQaRubricToolSchema = {
    name: 'archive_qa_rubric',
    description: 'Archive a QA rubric criterion so it stops being evaluated. It can be restored from the QA page, but loses its agent assignments.',
    inputSchema: z.object({ criterionId: z.string() }),
    outputSchema: z.object({ criterionId: z.string(), name: z.string() })
};
export const listSentimentLabelsToolSchema = {
    name: 'list_sentiment_labels',
    description: "List the organization's sentiment labels, of which the QA evaluator gives each conversation the best match.",
    inputSchema: z.object({}),
    outputSchema: z.object({ labels: z.array(sentimentLabelViewSchema) })
};
export const createSentimentLabelToolSchema = {
    name: 'create_sentiment_label',
    description: 'Create a sentiment label for the QA evaluator to choose from for the agents it is assigned to.',
    inputSchema: z.object({
        label: z.string().min(1).describe('Such as "Frustrated"'),
        description: z
            .string()
            .min(1)
            .describe('When the evaluator should choose it: the signals and behaviors that mark this sentiment'),
        color: z.enum(SENTIMENT_COLOR_NAMES),
        agents: agentsSchema,
        active: z.boolean().optional().describe('Defaults to true')
    }),
    outputSchema: z.object({ labelId: z.string(), label: z.string() })
};
export const updateSentimentLabelToolSchema = {
    name: 'update_sentiment_label',
    description: "Change a sentiment label. Leave out what shouldn't change.",
    inputSchema: createSentimentLabelToolSchema.inputSchema.partial().extend({ labelId: z.string() }),
    outputSchema: z.object({ labelId: z.string() })
};
export const archiveSentimentLabelToolSchema = {
    name: 'archive_sentiment_label',
    description: 'Archive a sentiment label so the evaluator stops using it. It can be restored from the QA page, but loses its agent assignments.',
    inputSchema: z.object({ labelId: z.string() }),
    outputSchema: z.object({ labelId: z.string(), label: z.string() })
};
export const describeRubric = (rubric) => ({
    criterionId: rubric.criterionId,
    name: rubric.name,
    ...(rubric.description && { description: rubric.description }),
    ...(rubric.prompt && { prompt: rubric.prompt }),
    passedLabel: rubric.passedLabel,
    failedLabel: rubric.failedLabel,
    active: rubric.activatedAt !== null,
    agentIds: rubric.assistantIds ?? []
});
export const describeSentimentLabel = (label, colorName) => ({
    labelId: label.labelId,
    label: label.label,
    ...(label.description && { description: label.description }),
    color: colorName(label.color),
    active: label.activatedAt !== null,
    agentIds: label.assistantIds ?? []
});
// The rubric endpoints read assistantIds as: [] for every agent, null for none, or specific ids.
const toRubricAssistantIds = (agents) => {
    if (agents === 'all')
        return [];
    return agents.length > 0 ? agents : null;
};
// The sentiment label endpoints take a scope instead.
const toAgentScope = (agents) => {
    if (agents === 'all')
        return { scope: 'all' };
    return agents.length > 0
        ? { scope: 'specific', assistantIds: agents }
        : { scope: 'none' };
};
/** The app's rubric body for a create or update call. Only what the call sets is sent. */
export const toRubricBody = ({ name, prompt, description, agents, passedLabel, failedLabel, active }) => ({
    ...(name !== undefined && { name }),
    ...(prompt !== undefined && { prompt }),
    ...(description !== undefined && { description }),
    ...(agents !== undefined && { assistantIds: toRubricAssistantIds(agents) }),
    ...(passedLabel !== undefined && { passedLabel }),
    ...(failedLabel !== undefined && { failedLabel }),
    ...(active !== undefined && { activated: active })
});
/** The app's sentiment label body for a create or update call, with the color as the app stores it. */
export const toSentimentLabelBody = ({ label, description, color, agents, active }, storedColor) => ({
    ...(label !== undefined && { label }),
    ...(description !== undefined && { description }),
    ...(color !== undefined && { color: storedColor(color) }),
    ...(agents !== undefined && { agentScope: toAgentScope(agents) }),
    ...(active !== undefined && { activated: active })
});
//# sourceMappingURL=quality.js.map